import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Package, MapPin, Calendar, Scale, Ruler, User, Truck, 
  MessageSquare, Send, CreditCard, CheckCircle, Clock, 
  ArrowLeft, Loader2, Star, FileText, Download, Timer
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { parcelsApi, messagesApi, paymentsApi, reviewsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatDateTime, formatPrice, getStatusLabel, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';
import { StarRating, RatingBadge } from '../../components/StarRating';
import ReviewModal from '../../components/ReviewModal';
import { VerifiedBadge } from '../../components/VerifiedBadge';
import NegotiationPanel from '../../components/NegotiationPanel';

const RouteMap = lazy(() => import('../../components/RouteMap'));
const LiveTrackingMap = lazy(() => import('../../components/LiveTrackingMap'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ParcelDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [parcel, setParcel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [paying, setPaying] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [revieweeInfo, setRevieweeInfo] = useState({ name: '', role: '' });
  const [deliveryCode, setDeliveryCode] = useState('');
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchParcel = useCallback(async () => {
    try {
      const response = await parcelsApi.getById(id);
      setParcel(response.data);
    } catch (error) {
      toast.error('Colis non trouvé');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await messagesApi.getByParcel(id);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [id]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await reviewsApi.getParcelReviews(id);
      setReviews(response.data.reviews || []);
      setCanReview(response.data.user_can_review || false);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [id]);

  const connectWebSocket = useCallback(() => {
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/api/ws/chat/${id}`);
    
    wsRef.current.onopen = () => {
      console.log('WebSocket connected');
    };
    
    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      setMessages((prev) => [...prev, message]);
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected');
    };
  }, [id]);

  useEffect(() => {
    fetchParcel();
    fetchMessages();
    fetchReviews();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchParcel, fetchMessages, fetchReviews]);

  useEffect(() => {
    if (parcel && (user.id === parcel.sender_id || user.id === parcel.carrier_id)) {
      connectWebSocket();
    }
  }, [parcel, user, connectWebSocket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    // Set reviewee info when parcel loads
    if (parcel && canReview) {
      if (user.id === parcel.sender_id) {
        setRevieweeInfo({ name: parcel.carrier_name, role: 'carrier' });
      } else {
        setRevieweeInfo({ name: parcel.sender_name, role: 'sender' });
      }
    }
  }, [parcel, canReview, user.id]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          sender_id: user.id,
          sender_name: user.name,
          content: newMessage,
        }));
        setNewMessage('');
      } else {
        await messagesApi.send({ parcel_id: id, content: newMessage });
        setNewMessage('');
        fetchMessages();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await parcelsApi.updateStatus(id, newStatus);
      toast.success('Statut mis à jour');
      fetchParcel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePayment = async () => {
    setPaying(true);
    try {
      const response = await paymentsApi.createSession({
        parcel_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      setPaying(false);
    }
  };

  const handleAccept = async () => {
    try {
      await parcelsApi.accept(id);
      toast.success('Mission acceptée!');
      fetchParcel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryCode || deliveryCode.length !== 6) {
      toast.error('Veuillez entrer le code de confirmation à 6 chiffres');
      return;
    }
    setConfirmingDelivery(true);
    try {
      await parcelsApi.confirmDelivery(id, deliveryCode);
      toast.success('Livraison confirmée avec succès!');
      setDeliveryCode('');
      fetchParcel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code incorrect');
    } finally {
      setConfirmingDelivery(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const response = await parcelsApi.downloadInvoice(id);
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_CF-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Facture téléchargée!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!parcel) return null;

  const canChat = user.id === parcel.sender_id || user.id === parcel.carrier_id;
  const isCarrier = user.role === 'carrier';
  const isSender = user.role === 'sender';

  // NOUVEAU FLUX: Créée → Payée → Acceptée → En cours → Livrée
  const statusSteps = [
    { key: 'created', label: 'Créée', icon: Package },
    { key: 'paid', label: 'Payée', icon: CreditCard },
    { key: 'accepted', label: 'Acceptée', icon: CheckCircle },
    { key: 'in_progress', label: 'En cours', icon: Truck },
    { key: 'delivered', label: 'Livrée', icon: MapPin },
  ];

  const currentStepIndex = statusSteps.findIndex(s => s.key === parcel.status);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="parcel-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate(-1)}
          className="border-slate-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900 uppercase tracking-tight">
            Détails de l&apos;envoi
          </h1>
          <p className="text-sm text-slate-500">ID: {parcel.id.slice(0, 8)}...</p>
        </div>
      </div>

      {/* Status Progress */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between overflow-x-auto">
            {statusSteps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index <= currentStepIndex;
              const isCurrent = index === currentStepIndex;
              return (
                <div key={step.key} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isCompleted 
                        ? 'bg-green-500 text-white' 
                        : 'bg-slate-100 text-slate-400'
                    } ${isCurrent ? 'ring-4 ring-green-200' : ''}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`text-xs mt-2 font-medium ${isCompleted ? 'text-green-600' : 'text-slate-400'}`}>
                      {step.label}
                    </span>
                  </div>
                  {index < statusSteps.length - 1 && (
                    <div className={`w-12 md:w-24 h-1 mx-2 ${
                      index < currentStepIndex ? 'bg-green-500' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Parcel Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Route */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
                Itinéraire
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="w-0.5 h-16 bg-slate-200 my-2" />
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Départ</p>
                    <p className="font-medium text-slate-900">{parcel.pickup_address.street}</p>
                    <p className="text-slate-600">{parcel.pickup_address.postal_code} {parcel.pickup_address.city}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Arrivée</p>
                    <p className="font-medium text-slate-900">{parcel.delivery_address.street}</p>
                    <p className="text-slate-600">{parcel.delivery_address.postal_code} {parcel.delivery_address.city}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-900" data-testid="parcel-distance">{parcel.distance_km} km</p>
                  <p className="text-sm text-slate-500">Distance</p>
                  {parcel.duration_text && (
                    <div className="mt-2 flex items-center justify-end gap-1 text-orange-600">
                      <Timer className="w-4 h-4" />
                      <span className="text-sm font-semibold" data-testid="parcel-duration">{parcel.duration_text}</span>
                    </div>
                  )}
                  {!parcel.duration_text && parcel.distance_km && (
                    <div className="mt-2 flex items-center justify-end gap-1 text-orange-600">
                      <Timer className="w-4 h-4" />
                      <span className="text-sm font-semibold" data-testid="parcel-duration-estimated">
                        ~{Math.ceil(parcel.distance_km / 80)}h{String(Math.round((parcel.distance_km / 80 % 1) * 60)).padStart(2, '0')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Mini-carte itinéraire */}
              <div className="mt-4" data-testid="route-map-container">
                <Suspense fallback={
                  <div className="h-[200px] bg-slate-100 rounded-lg flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                }>
                  <RouteMap 
                    pickupCity={parcel.pickup_address?.city}
                    deliveryCity={parcel.delivery_address?.city}
                    pickupLat={parcel.pickup_address?.lat}
                    pickupLng={parcel.pickup_address?.lng}
                    deliveryLat={parcel.delivery_address?.lat}
                    deliveryLng={parcel.delivery_address?.lng}
                  />
                </Suspense>
              </div>
            </CardContent>
          </Card>

          {/* Details */}
          {/* Live Tracking - Only when in_progress */}
          {parcel.status === 'in_progress' && (
            <Card className="border-orange-200 bg-orange-50/30">
              <CardContent className="p-6">
                <Suspense fallback={
                  <div className="h-[250px] bg-slate-100 rounded-lg flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
                  </div>
                }>
                  <LiveTrackingMap
                    parcelId={parcel.id}
                    pickupCity={parcel.pickup_address?.city}
                    deliveryCity={parcel.delivery_address?.city}
                    isCarrier={user?.role === 'carrier'}
                  />
                </Suspense>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
                Détails du colis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Scale className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{parcel.weight} kg</p>
                  <p className="text-xs text-slate-500">Poids</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Ruler className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-lg font-bold text-slate-900">
                    {parcel.dimensions.length}x{parcel.dimensions.width}x{parcel.dimensions.height}
                  </p>
                  <p className="text-xs text-slate-500">Dimensions (cm)</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-lg font-bold text-slate-900">{formatDate(parcel.pickup_date)}</p>
                  <p className="text-xs text-slate-500">Date enlèvement</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Clock className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-lg font-bold text-slate-900">{formatDate(parcel.created_at)}</p>
                  <p className="text-xs text-slate-500">Créé le</p>
                </div>
              </div>
              {parcel.description && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Description</p>
                  <p className="text-slate-900">{parcel.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Negotiation Panel - Show for paid parcels without carrier */}
          {parcel.status === 'paid' && !parcel.carrier_id && (
            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-orange-500" />
                  Offres & Négociation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <NegotiationPanel 
                  parcelId={id}
                  senderBudget={parcel.proposed_price}
                  userRole={user.role}
                  onOfferAccepted={fetchParcel}
                />
              </CardContent>
            </Card>
          )}

          {/* Chat Section */}
          {canChat && parcel.status !== 'created' && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-orange-500" />
                  Messagerie
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] overflow-y-auto border border-slate-100 rounded-lg p-4 mb-4 bg-slate-50">
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      <p>Aucun message. Commencez la conversation!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            msg.sender_id === user.id 
                              ? 'bg-slate-900 text-white' 
                              : 'bg-white border border-slate-200 text-slate-900'
                          }`}>
                            <p className={`text-xs mb-1 ${msg.sender_id === user.id ? 'text-slate-300' : 'text-slate-500'}`}>
                              {msg.sender_name}
                            </p>
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-slate-400' : 'text-slate-400'}`}>
                              {formatDateTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Votre message..."
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Price Card */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm text-slate-500 uppercase tracking-wide mb-2">Prix total (expediteur)</p>
                <p className="text-4xl font-bold text-orange-500">{formatPrice(parcel.total_price)}</p>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Prix negocie</span>
                    <span>{formatPrice(parcel.final_price || parcel.proposed_price)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Commission plateforme (15%)</span>
                    <span>{formatPrice(parcel.commission)}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between font-medium text-slate-900">
                    <span>Total expediteur</span>
                    <span>{formatPrice(parcel.total_price)}</span>
                  </div>
                  {parcel.tva_amount > 0 && (
                    <>
                      <div className="border-t border-slate-200 pt-2 flex justify-between text-slate-500">
                        <span>TVA (20%)</span>
                        <span>-{formatPrice(parcel.tva_amount)}</span>
                      </div>
                      <div className="flex justify-between font-medium text-green-600">
                        <span>Verse au cotransporteur</span>
                        <span>{formatPrice(parcel.carrier_payout)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Code Card - Only visible to sender */}
          {isSender && parcel.delivery_code && ['paid', 'accepted', 'in_progress'].includes(parcel.status) && (
            <Card className="border-orange-300 bg-orange-50" data-testid="delivery-code-card">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-orange-700 font-semibold uppercase tracking-wide mb-2">Code de confirmation</p>
                  <p className="text-4xl font-mono font-bold text-orange-600 tracking-[0.3em]" data-testid="delivery-code-display">{parcel.delivery_code}</p>
                  <p className="text-xs text-orange-600 mt-3">Donnez ce code au cotransporteur a la livraison pour valider la reception</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Users Card */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
                Participants
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-500">Expéditeur</p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">{parcel.sender_name}</p>
                    <VerifiedBadge verified={parcel.sender_verified} size="sm" />
                  </div>
                </div>
              </div>
              {parcel.carrier_name ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Transporteur</p>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{parcel.carrier_name}</p>
                      <VerifiedBadge verified={parcel.carrier_verified} size="sm" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 opacity-50">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Transporteur</p>
                    <p className="font-medium text-slate-400">En attente...</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
                Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Carrier actions */}
              {isCarrier && parcel.status === 'created' && (
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  onClick={handleAccept}
                  data-testid="accept-mission-btn"
                >
                  Accepter la mission
                </Button>
              )}
              
              {isCarrier && parcel.carrier_id === user.id && parcel.status === 'accepted' && (
                <Button 
                  className="w-full bg-blue-500 hover:bg-blue-600"
                  onClick={() => handleUpdateStatus('in_progress')}
                  disabled={updatingStatus}
                  data-testid="start-delivery-btn"
                >
                  {updatingStatus ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  Démarrer la livraison
                </Button>
              )}
              
              {isCarrier && parcel.carrier_id === user.id && parcel.status === 'in_progress' && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">Entrez le code de confirmation donné par l'expéditeur :</p>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      maxLength={6}
                      placeholder="Code à 6 chiffres"
                      value={deliveryCode}
                      onChange={(e) => setDeliveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-lg font-mono tracking-widest"
                      data-testid="delivery-code-input"
                    />
                    <Button
                      className="bg-green-500 hover:bg-green-600"
                      onClick={handleConfirmDelivery}
                      disabled={confirmingDelivery || deliveryCode.length !== 6}
                      data-testid="confirm-delivery-btn"
                    >
                      {confirmingDelivery ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                    </Button>
                  </div>
                </div>
              )}

              {/* NOUVEAU FLUX: Paiement après création, avant acceptation */}
              {isSender && parcel.status === 'created' && (
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  onClick={handlePayment}
                  disabled={paying}
                  data-testid="pay-now-btn"
                >
                  {paying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Redirection...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Payer pour publier ({formatPrice(parcel.total_price)})
                    </>
                  )}
                </Button>
              )}

              {/* Message si en attente de paiement */}
              {isSender && parcel.status === 'created' && (
                <p className="text-sm text-amber-600 text-center">
                  ⚠️ Payez pour que votre colis soit visible par les cotransporteurs
                </p>
              )}

              {/* Message si payé mais pas encore accepté */}
              {parcel.status === 'paid' && !parcel.carrier_id && (
                <div className="text-center py-4 bg-blue-50 rounded-lg">
                  <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="font-medium text-blue-600">En attente d&apos;un cotransporteur</p>
                  <p className="text-sm text-blue-500">Votre colis est visible par les cotransporteurs</p>
                </div>
              )}

              {parcel.status === 'delivered' && (
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="font-medium text-green-600">Mission terminée</p>
                </div>
              )}

              {/* Download Invoice Button - For sender only, when parcel is paid */}
              {isSender && ['paid', 'accepted', 'in_progress', 'delivered'].includes(parcel.status) && (
                <Button 
                  variant="outline"
                  className="w-full border-slate-200 hover:bg-slate-50"
                  onClick={handleDownloadInvoice}
                  disabled={downloadingInvoice}
                  data-testid="download-invoice-btn"
                >
                  {downloadingInvoice ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 mr-2" />
                      Télécharger la facture
                    </>
                  )}
                </Button>
              )}

              {/* Review Button */}
              {canReview && (
                <Button 
                  className="w-full bg-yellow-500 hover:bg-yellow-600"
                  onClick={() => setShowReviewModal(true)}
                  data-testid="leave-review-btn"
                >
                  <Star className="w-5 h-5 mr-2" />
                  Laisser un avis
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Reviews Card */}
          {reviews.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Avis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-900 text-sm">{review.reviewer_name}</span>
                      <StarRating rating={review.rating} size="sm" />
                    </div>
                    {review.comment && (
                      <p className="text-sm text-slate-600">{review.comment}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">{formatDate(review.created_at)}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        parcelId={id}
        revieweeName={revieweeInfo.name}
        revieweeRole={revieweeInfo.role}
        onSuccess={() => {
          fetchReviews();
          setCanReview(false);
        }}
      />
    </div>
  );
};

export default ParcelDetailPage;
